import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, AlertCircle, Loader2, Lock } from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useHasRedeemedKey } from '@/features/solution-browser/hooks/useActivationKey'
import { UnlockDialog } from '@/features/payments'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'
import NodeQuestionBrowser from '../components/NodeQuestionBrowser'
import { useSubjectTree } from '../hooks/usePYQ'
import { SYLLABUS_DESKTOP_QUERY, toSubjectCode, findBrowsableNode } from '../utils/syllabusSubjects'

/**
 * Full-screen PYQ browser for one syllabus node, used below the desktop breakpoint.
 * Tablets get list + reading pane; phones get the list, then the question as its own view.
 * URL: ?subject=<subject node code>&node=<node code>&q=<question number>
 */
export default function PYQSyllabusBrowserPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isDesktop = useMediaQuery(SYLLABUS_DESKTOP_QUERY)
  const isWide = useMediaQuery('(min-width: 768px)')
  const userId = useAuthStore((s) => s.userId) ?? undefined
  const access = useHasRedeemedKey(userId)
  const isAccountLoading = access.isLoading
  // Free user = no REDEEMED activation key (no test series bought). Unknown on error, so don't lock then.
  const isFreeUser = !access.isLoading && !access.isError && !access.hasRedeemedKey
  const [showUnlock, setShowUnlock] = useState(false)

  const subjectCode = toSubjectCode(searchParams.get('subject'))
  const nodeCode = searchParams.get('node')
  const treeQuery = useSubjectTree(subjectCode)
  const subject = treeQuery.data
  const isLoading = treeQuery.isLoading
  const isError = treeQuery.isError || access.isError
  const refetch = () => {
    if (treeQuery.isError) treeQuery.refetch()
    if (access.isError) access.refetch()
  }
  const node = findBrowsableNode(subject, nodeCode)

  useEffect(() => {
    if (!isAccountLoading && isFreeUser) setShowUnlock(true)
  }, [isAccountLoading, isFreeUser])

  // Desktop shows tree, list and question together on the By Syllabus page
  if (isDesktop) {
    return <Navigate to={`/question-bank/by-syllabus?${searchParams}`} replace />
  }

  const goBack = () => navigate(`/question-bank/by-syllabus?${new URLSearchParams({ subject: subjectCode })}`)

  const header = (
    <header className="z-10 flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center gap-3">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-gray-600 transition-colors hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Back</span>
        </button>
        <h1 className="min-w-0 truncate text-lg font-semibold text-gray-900">{node?.nodeName ?? 'Browse PYQ'}</h1>
      </div>
    </header>
  )

  // The question browser renders the header itself (it hides it on phones while a question is open)
  const showsBrowser = !!node && !isFreeUser && !isAccountLoading && !access.isError
  let body
  if (node && showsBrowser) {
    body = (
      <NodeQuestionBrowser
        key={node.nodeCode}
        nodeCode={node.nodeCode}
        nodeName={node.nodeName}
        isWide={isWide}
        header={header}
      />
    )
  } else if (isLoading || isAccountLoading) {
    body = (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    )
  } else {
    body = (
      <div className="flex flex-1 items-center justify-center p-6 text-center">
        <div>
          {isFreeUser ? (
            <>
              <Lock className="mx-auto mb-3 h-10 w-10 text-gray-400" />
              <h2 className="font-medium text-gray-800">Syllabus-wise PYQs are locked</h2>
              <button onClick={() => setShowUnlock(true)} className="mt-2 text-sm font-medium text-blue-600 underline">
                See how to unlock
              </button>
            </>
          ) : (
            <>
              <AlertCircle className="mx-auto mb-3 h-10 w-10 text-yellow-500" />
              <h2 className="font-medium text-gray-800">
                {isError ? "Couldn't load the syllabus" : 'This unit or topic was not found'}
              </h2>
              <button
                onClick={() => (isError ? refetch() : goBack())}
                className="mt-2 text-sm font-medium text-blue-600 underline"
              >
                {isError ? 'Try again' : 'Back to syllabus'}
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-gray-50">
      {!showsBrowser && header}
      {body}
      <UnlockDialog
        isOpen={showUnlock}
        onClose={() => setShowUnlock(false)}
        title="Unlock syllabus-wise PYQs"
        description="Practise previous year questions for every unit and topic."
        source="pyq_by_syllabus"
      />
    </div>
  )
}
